/* eslint-disable no-console */
/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MapContainer, Marker, TileLayer, Polyline } from "react-leaflet";
import { Col, Row } from "antd";
import CustomLayout from "../../../Core/Layout/CustomLayout";
import { INITIAL_ZOOM_LEVEL, MAX_BOUNDS } from "../../Home/Data/HomeData";
import getOngoingSessionIcon from "./getOngoingSessionIcon";
import ZoomButton, {
  ExpandButton,
  RelocateButton,
} from "../../../Core/Components/MapMarkerButtons";
import getSessionStatus from "../getSessionStatus";
import { SESSION_STATUS, SESSION_TYPE } from "../constants";
import SessionFullScreenMap from "./SessionFullScreenMap";
import OverviewPart from "./OverviewPart";
import SessionChecklistModal from "./SessionChecklistModal";
import { updateSession } from "../../../Redux/Slices/sessions.slice";
import ListDropdown from "../ListDropdown";
import { renderPolygonPoint } from "../MeasureLand/renderIcons";
import getCenterOfPolygon from "../../../Utils/getCenterOfPolygon";
import { client } from "../../../Utils/axiosClient";

const lineOptions = {
  color: "#085054",
};

const SessionDetailScreen = () => {
  const dispatch = useDispatch();

  const { id } = useParams();

  const { currentSession, checklistModal } = useSelector(
    (state) => state.sessions,
  );

  const [fullScreen, setFullScreen] = useState(false);
  const [sessionChecklist, setSessionChecklist] = useState([]);

  const mapRef = useRef(null);

  const getAssetLatLngs = () => {
    // show the center of marked area for tilling sessions
    if (
      currentSession?.activityType == SESSION_TYPE.TILLING &&
      currentSession?.status == SESSION_STATUS.COMPLETED &&
      currentSession?.areaCoords?.length >= 3
    ) {
      return getCenterOfPolygon(currentSession.areaCoords);
    }

    // show the farm lat and long point as the center
    if (currentSession?.farmLat && currentSession?.farmLong) {
      return [
        parseFloat(currentSession.farmLat),
        parseFloat(currentSession.farmLong),
      ];
    }

    // default center
    return [-0.6803741, 34.7569482];
  };

  const getChecklist = useCallback(async () => {
    try {
      const response = await client.get(
        `${process.env.REACT_APP_API_URL}/session/summary-checklist`,
        {
          params: {
            sessionId: currentSession.id,
          },
        },
      );

      const checklist = response.data.checklist?.map((item) => ({
        id: item.partId,
        name: item.partName,
        passed: item.passed,
        mandatory: item.mandatory,
      }));

      setSessionChecklist([...checklist]);
    } catch (error) {
      console.log(error);
    }
  }, [currentSession]);

  // to get checklist for the current session (but not for upcoming session)
  useEffect(() => {
    if (currentSession?.status !== SESSION_STATUS.UPCOMING) {
      getChecklist();
    }
  }, [currentSession, getChecklist]);

  return (
    <CustomLayout pageName="Sessions" nestedName={`${id}`}>
      <Row className="w-full h-full">
        <Col span={16}>
          <div className="w-full bg-background-white py-4 px-6 border-b border-light-grey">
            <div className="flex items-center justify-between">
              <p className="text-base font-medium font-poppins">Overview</p>

              <div className="flex items-center gap-x-4">
                <div>{getSessionStatus(currentSession?.status)}</div>

                {currentSession?.status === SESSION_STATUS.UPCOMING ? (
                  <div
                    role="button"
                    className="w-8 h-6 rounded-md flex items-center justify-center bg-light-grey hover:bg-background-grey-hover"
                  >
                    <ListDropdown detail={currentSession} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <OverviewPart sessionChecklist={sessionChecklist} />
        </Col>

        <Col span={8}>
          <div className="bg-background-white py-4 px-6 border-l border-light-grey">
            <p className="font-poppins font-medium text-base">Location</p>
          </div>

          <div className="border-l border-light-grey relative">
            <div className="absolute right-6 top-6 z-30">
              <ExpandButton onClick={() => setFullScreen(true)} />
            </div>

            <div className="h-[calc(100vh-128px)]">
              <MapContainer
                center={getAssetLatLngs()}
                zoom={INITIAL_ZOOM_LEVEL}
                maxBounds={MAX_BOUNDS}
                className="z-10 w-full h-full"
                zoomControl={false}
                ref={mapRef}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                <Marker
                  position={getAssetLatLngs()}
                  icon={getOngoingSessionIcon()}
                />

                {/* show the marked points for tilling sessions -> only if session is completed */}
                {currentSession?.activityType == SESSION_TYPE.TILLING &&
                  currentSession?.status == SESSION_STATUS.COMPLETED &&
                  currentSession?.areaCoords?.length &&
                  currentSession?.areaCoords.map((item) => (
                    <Marker
                      key={`${item}`}
                      position={item}
                      icon={renderPolygonPoint()}
                    />
                  ))}

                {/* show the polyline with marked points for tilling session -> only if session is completed */}
                {currentSession?.activityType == SESSION_TYPE.TILLING &&
                  currentSession?.status == SESSION_STATUS.COMPLETED &&
                  currentSession?.areaCoords?.length && (
                    <Polyline
                      pathOptions={lineOptions}
                      positions={currentSession?.areaCoords}
                      weight={3}
                    />
                  )}
              </MapContainer>
            </div>

            <div className="absolute bottom-6 right-6 z-30">
              <div className="flex flex-col gap-y-3">
                <RelocateButton
                  mapRef={mapRef}
                  initialZoomlevel={INITIAL_ZOOM_LEVEL}
                  center={getAssetLatLngs()}
                />
                <ZoomButton mapRef={mapRef} />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <SessionFullScreenMap
        visible={fullScreen}
        onCancel={() => setFullScreen(false)}
      />

      <SessionChecklistModal
        visible={checklistModal}
        data={sessionChecklist}
        onCancel={() =>
          dispatch(
            updateSession({
              key: "checklistModal",
              value: false,
            }),
          )
        }
      />
    </CustomLayout>
  );
};

export default SessionDetailScreen;
